import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import './EmailVerification.css';

const EmailVerification = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [status, setStatus] = useState('verifying'); // 'verifying', 'success', 'error'
  const [message, setMessage] = useState('');
  
  useEffect(() => {
    const token = searchParams.get('token');
    const type = searchParams.get('type');
    const verified = searchParams.get('verified');

    // If already verified (from Edge Function redirect)
    if (verified === 'true') {
      setStatus('success');
      setMessage('メールアドレスが正常に確認されました。');
      
      // Redirect to dashboard after 3 seconds
      setTimeout(() => {
        navigate('/dashboard', { replace: true });
      }, 3000);
      return;
    }

    // If no token, show error
    if (!token) {
      setStatus('error');
      setMessage('確認トークンが見つかりません。');
      return;
    }

    // Verify email using Supabase
    verifyEmail(token, type);
  }, [searchParams, navigate]);

  const verifyEmail = async (token, type = 'email') => {
    try {
      setStatus('verifying');
      setMessage('メールアドレスを確認しています...');

      // Call our Edge Function for verification
      const response = await fetch(`${process.env.REACT_APP_SUPABASE_URL}/functions/v1/auth-verify`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${process.env.REACT_APP_SUPABASE_ANON_KEY}`
        },
        body: JSON.stringify({ token, type }),
        credentials: 'include'
      });

      const data = await response.json();

      if (response.ok && data.success) {
        setStatus('success');
        setMessage('メールアドレスが正常に確認されました！');
        
        // Redirect to dashboard after 3 seconds
        setTimeout(() => {
          navigate('/dashboard', { replace: true });
        }, 3000);
      } else {
        throw new Error(data.message || '確認に失敗しました');
      }

    } catch (error) {
      console.error('Email verification error:', error);
      setStatus('error');
      setMessage(error.message || 'メール確認中にエラーが発生しました。');
    }
  };

  const handleReturnToLogin = () => {
    navigate('/login', { replace: true });
  };

  const handleResendVerification = () => {
    // Redirect to login page with resend request
    navigate('/login?resend=true', { replace: true });
  };

  // If user is already logged in, redirect to dashboard
  if (user) {
    navigate('/dashboard', { replace: true });
    return null;
  }

  return (
    <div className="email-verification">
      <div className="verification-container">
        <div className="verification-content">
          
          {/* Logo/Header */}
          <div className="verification-header">
            <h1 className="verification-title">メール確認</h1>
          </div>

          {/* Status Icon */}
          <div className="verification-status">
            {status === 'verifying' && (
              <div className="status-icon verifying">
                <div className="spinner"></div>
              </div>
            )}
            
            {status === 'success' && (
              <div className="status-icon success">
                <svg width="64" height="64" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" stroke="#10B981" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </div>
            )}
            
            {status === 'error' && (
              <div className="status-icon error">
                <svg width="64" height="64" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" stroke="#EF4444" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </div>
            )}
          </div>

          {/* Message */}
          <div className="verification-message">
            <p>{message}</p>
          </div>

          {/* Actions */}
          <div className="verification-actions">
            {status === 'success' && (
              <div className="success-info">
                <p>3秒後にダッシュボードにリダイレクトされます...</p>
                <button 
                  onClick={() => navigate('/dashboard', { replace: true })}
                  className="btn btn-primary"
                >
                  今すぐダッシュボードへ
                </button>
              </div>
            )}

            {status === 'error' && (
              <div className="error-actions">
                <button 
                  onClick={handleResendVerification}
                  className="btn btn-secondary"
                >
                  確認メールを再送信
                </button>
                <button 
                  onClick={handleReturnToLogin}
                  className="btn btn-primary"
                >
                  ログインページに戻る
                </button>
              </div>
            )}

            {status === 'verifying' && (
              <div className="verifying-info">
                <p>しばらくお待ちください...</p>
              </div>
            )}
          </div>

        </div>
      </div>
    </div>
  );
};

export default EmailVerification;